import {Component, Inject} from '@angular/core';
import { CommonModule } from '@angular/common';
import {Product} from "../../product-details/interface/product";
import {FabricProfileItemList} from "../../product-details/interface/fabric-profile-item-list";
import {SizeProfileOptionItem} from "../../product-details/interface/size-profile-option-item";
import {FinishProfileItem} from "../../product-details/interface/finish-profile-item-list";
import {MAT_DIALOG_DATA, MatDialogRef} from "@angular/material/dialog";
import {VolumeDiscountProfileItem} from "../../product-details/interface/volume-discount-profile-item";
import {ProductBulkDiscountComponent} from "../product-bulk-discount/product-bulk-discount.component";
import {ProductCompositionComponent} from "../product-composition/product-composition.component";
import {ProductPriceComponent} from "../product-price/product-price.component";
import {ProductSkuComponent} from "../product-sku/product-sku.component";
import {ProductQuantityInputComponent} from "../product-quantity-input/product-quantity-input.component";
import {ProductSizeProfileComponent} from "../product-size-profile/product-size-profile.component";
import {ProductCartButtonsComponent} from "../product-cart-buttons/product-cart-buttons.component";
import {
	ProductCustomFabricProfileComponent
} from "../product-custom-fabric-profile/product-custom-fabric-profile.component";
import {ProductFinishProfileComponent} from "../product-finish-profile/product-finish-profile.component";
import {
	ProductPreOrderQuantityInputComponent
} from "../product-pre-order-quantity-input/product-pre-order-quantity-input.component";
import {ProductInformationService} from "../product-information-service";
import {PipeModule} from "../../pipe/pipe.module";
import {ProductCartService} from "../product-cart.service";

@Component({
	selector: 'fb-product-pre-order-dialog',
	standalone: true,
	imports: [CommonModule, ProductBulkDiscountComponent, ProductPriceComponent, ProductSkuComponent, ProductSizeProfileComponent, ProductCustomFabricProfileComponent, ProductFinishProfileComponent, ProductPreOrderQuantityInputComponent, PipeModule],
	templateUrl: './product-pre-order-dialog.component.html',
	styleUrls: ['./product-pre-order-dialog.component.scss']
})
export class ProductPreOrderDialogComponent {
	public fromDays: number = 0;
	public toDays: number = 0;
	public quantityFromUser: number = 0;
	public quantityInStock: number = 0;
	public minimumOrderQuantity: number = 50;
	public calculatedPrice: number = 0;
	public calculatedDiscountPrice: number = 0;
	public stockMessage: string = '';
	public selectedFabric: FabricProfileItemList | undefined;
	public selectedSize: SizeProfileOptionItem = {} as SizeProfileOptionItem;
	public selectedFinishes: FinishProfileItem[] = [];
	public customSize: any;
	public customizationSelected: boolean = false;
	public selectFinishPrice: number = 0;
	public customSizePrice: number = 0;

	constructor(
		@Inject(MAT_DIALOG_DATA) public productResponse: { product: Product, id: number },
		public dialogRef: MatDialogRef<ProductPreOrderDialogComponent>,
		public productService: ProductInformationService,
		private _productCartService: ProductCartService
	) {
		this.minimumOrderQuantity = this.calculateMinimumOrderQuantity();
		const selectedFabricPrice: number = (this.productResponse.product.madeToOrderFabric) ? this.productResponse.product.madeToOrderFabric.price : 0;
		this.calculatePrice(selectedFabricPrice, 0, 0);
	}

	public calculateMinimumOrderQuantity(): number {
		if (this.productResponse.product.volumeDiscountProfileEnabled && this.productResponse.product.volumeDiscountProfile) {
			const vDList: VolumeDiscountProfileItem[] = this.productResponse.product.volumeDiscountProfile.volumeDiscountProfileItemList.sort((a, b) => a.minimumOrderQuantity - b.minimumOrderQuantity);
			const selectedVD: VolumeDiscountProfileItem | undefined = vDList.find(item => item.preOrder);
			if (selectedVD) {
				this.toDays = new Date().setDate(new Date().getDate() + selectedVD.deliveryToDays);
				this.fromDays = new Date().setDate(new Date().getDate() + selectedVD.deliveryFromDays);
				return selectedVD.minimumOrderQuantity;
			}
		}
		return 50;
	}

	public reCalculatePrice(): void {
		let selectedFabricPrice: number = 0;
		let customSizePrice: number = 0;
		let finishPrice: number = 0;
		if (this.selectedFabric) {
			selectedFabricPrice = this.selectedFabric.fabricPreview.price;
		} else {
			selectedFabricPrice = (this.productResponse.product.madeToOrderFabric) ? this.productResponse.product.madeToOrderFabric.price : this.productResponse.product.price;
		}

		if (this.customSize && Object.keys(this.customSize).length > 0 && this.productResponse.product.customSizeProfileEnabled && this.productResponse.product.customSizeProfile) {
			customSizePrice = this.productResponse.product.customSizeProfile.price;
		}
		if (this.selectedFinishes.length > 0) {
			this.selectedFinishes.forEach(item => finishPrice += item.price);
		}
		this.customizationSelected = !!(this.selectedFabric || this.customSize || this.selectedFinishes.length > 0);
		this.calculatePrice(selectedFabricPrice, finishPrice, customSizePrice, this.customizationSelected);
	}

	public calculatePrice(
		selectedFabricPrice: number,
		selectedFinishPrice: number,
		customSizePrice: number,
		customizationSelected: boolean = false
	): void {
		if (this.productResponse.product.productGroup !== 'finished') {
			this.quantityInStock = this.productService.calculateFabricProductQuantity(
				this.productResponse.product,
				this.selectedFabric,
				customizationSelected
			);
			this.calculatedPrice = this.productService.calculateFabricProductPrice(
				this.productResponse.product,
				selectedFabricPrice,
				selectedFinishPrice,
				customSizePrice
			);
		} else {
			this.quantityInStock = this.productService.calculateFinishProductQuantity(
				this.productResponse.product,
				this.selectedFabric,
				this.selectedSize,
				!!customSizePrice,
				customizationSelected
			);
			this.calculatedPrice = this.productService.calculateFinishProductPrice(
				this.productResponse.product,
				selectedFabricPrice,
				selectedFinishPrice,
				customSizePrice
			);
		}
		this.selectFinishPrice = selectedFinishPrice;
		this.customSizePrice = customSizePrice;
		this.calculateVDPrice();
		this.stockMessage = this.productService.getStockMessage();
	}

	public onCustomSizeSelected($event: any): void {
		this.customSize = $event;
		this.reCalculatePrice();
	}

	public fabricSelected($event: FabricProfileItemList | undefined): void {
		this.selectedFabric = $event;
		this.reCalculatePrice();
	}

	public finishSelectionChange($event: any): void {
		this.selectedFinishes = $event;
		this.reCalculatePrice();
	}

	public onSizeSelected($event: any): void {
		this.selectedSize = $event;
		this.reCalculatePrice();
	}

	public onQuantityChange($event: number): void {
		this.quantityFromUser = $event;
		this.calculateVDPrice();
	}

	public calculateVDPrice(): void {
		if (this.productResponse.product.volumeDiscountProfileEnabled && this.productResponse.product.volumeDiscountProfile) {
			const vDList: VolumeDiscountProfileItem[] = this.productResponse.product.volumeDiscountProfile.volumeDiscountProfileItemList.sort((a, b) => b.minimumOrderQuantity - a.minimumOrderQuantity);
			for (const item of vDList) {
				if (item.minimumOrderQuantity <= this.quantityFromUser) {
					this.toDays = new Date().setDate(new Date().getDate() + item.deliveryToDays);
					this.fromDays = new Date().setDate(new Date().getDate() + item.deliveryFromDays);
					this.calculatedDiscountPrice = this.productService.calculateVDProductPrice(
						this.productResponse.product,
						this.selectedFabric,
						this.selectFinishPrice,
						this.customSizePrice,
						item,
						item.minimumOrderQuantity
					);
					break;
				}
			}
		}
	}

	public addPreOrderToCart(): void {
		if(this.productResponse.product.productGroup === 'finished') {
			this._productCartService.addFinishedProduct(
				this.productResponse.id,
				this.productResponse.product,
				this.quantityFromUser,
				this.productService.getMakingCharges(),
				this.customSize? this.customSize : {},
				this.selectedFabric? this.selectedFabric.fabricPreview.id : this.productResponse.product.fabricProfileEnabled && this.productResponse.product.madeToOrderFabric? this.productResponse.product.madeToOrderFabric.id : 0,
				this.selectedSize?.id ?? 0,
				'PRE_ORDER',
				this.selectedFinishes.map((item) => item.id).join(','),
				this.calculatedPrice
			);
		}
		else {
			this._productCartService.addFabricProduct(
				this.productResponse.id,
				this.productResponse.product,
				this.quantityFromUser,
				this.productService.getMakingCharges(),
				this.customSize? this.customSize : {},
				this.selectedFabric? this.selectedFabric.fabricPreview.id : this.productResponse.product.fabricProfileEnabled && this.productResponse.product.madeToOrderFabric? this.productResponse.product.madeToOrderFabric.id : 0,
				this.selectedSize?.id ?? 0,
				'PRE_ORDER',
				this.selectedFinishes.map((item) => item.id).join(','),
				this.calculatedPrice
			);
		}
	}
}
